import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Trash2,
  Save,
  UploadCloud,
  Loader2,
  Film,
  CheckCircle2,
  AlertTriangle,
  GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import {
  listStorySections,
  createStorySection,
  updateStorySection,
  deleteStorySection,
  getStorySection,
  uploadSectionVideo,
  reorderStorySections,
  mediaUrl,
} from "@/api/client";

const STATUS = {
  empty: { label: "No video", cls: "bg-white/5 text-amber-100/40", Icon: Film },
  uploading: { label: "Uploading…", cls: "bg-sky-500/15 text-sky-300", Icon: Loader2 },
  processing: { label: "Processing…", cls: "bg-amber-500/15 text-amber-300", Icon: Loader2 },
  ready: { label: "Ready", cls: "bg-emerald-500/15 text-emerald-300", Icon: CheckCircle2 },
  failed: { label: "Failed", cls: "bg-red-500/15 text-red-300", Icon: AlertTriangle },
};

const Field = ({ label, textarea, ...props }) => (
  <div>
    <label className="mb-1.5 block text-xs text-amber-100/50">{label}</label>
    {textarea ? (
      <textarea
        {...props}
        rows={2}
        className="w-full resize-none rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm outline-none focus:border-amber-300/50"
      />
    ) : (
      <input
        {...props}
        className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm outline-none focus:border-amber-300/50"
      />
    )}
  </div>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <div>
    <label className="mb-1.5 block text-xs text-amber-100/50">{label}</label>
    <select
      data-testid={testId}
      value={value}
      onChange={onChange}
      className="w-full rounded-lg border border-amber-300/15 bg-[#080b16]/60 px-3 py-2 text-sm outline-none focus:border-amber-300/50"
    >
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#0d1226]">
          {o}
        </option>
      ))}
    </select>
  </div>
);

const SceneSkeleton = ({ s, index }) => {
  const ready = s.status === "ready" && s.thumbnail_path;
  const pos = s.overlay_position || "left";
  const align =
    pos === "center"
      ? "items-center justify-center text-center"
      : pos === "bottom"
      ? "items-center justify-end text-center"
      : "items-start justify-end text-left";
  const centerBar = pos === "left" ? "" : "mx-auto";
  return (
    <div
      data-testid={`scene-skeleton-${index}`}
      className="relative mb-2 aspect-video overflow-hidden rounded-lg border border-amber-300/15 bg-gradient-to-br from-[#0b1330] to-[#05070f]"
    >
      {ready ? (
        <img
          src={mediaUrl(s.thumbnail_path)}
          alt="preview"
          data-testid={`story-thumb-${index}`}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
      ) : (
        <div className="absolute inset-0 grid place-items-center text-amber-100/15">
          <Film className="h-7 w-7" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/25" />
      <span className="absolute left-2 top-2 z-10 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-amber-200">
        {index + 1}
      </span>
      <span className="absolute right-2 top-2 z-10 rounded bg-black/55 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-amber-100/70">
        {pos}
      </span>
      <div className={`absolute inset-0 z-[5] flex flex-col p-3 ${align}`}>
        <div className="max-w-[85%]">
          {s.headline ? (
            <p className={`truncate font-display text-[11px] leading-tight text-white ${centerBar}`}>
              {s.headline}
            </p>
          ) : (
            <div className={`h-2 w-20 rounded bg-white/70 ${centerBar}`} />
          )}
          <div className={`mt-1 h-1.5 w-28 max-w-full rounded bg-white/25 ${centerBar}`} />
          <div className={`mt-1 h-1.5 w-16 rounded bg-white/20 ${centerBar}`} />
        </div>
      </div>
    </div>
  );
};

const StorySectionCard = ({ section, index, onDeleted, dragHandleProps }) => {
  const [s, setS] = useState(section);
  const [saving, setSaving] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const fileRef = useRef(null);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  // Poll while processing
  const poll = useCallback(async () => {
    try {
      const fresh = await getStorySection(s.id);
      setS((p) => ({ ...p, ...fresh }));
      return fresh.status;
    } catch {
      return null;
    }
  }, [s.id]);

  useEffect(() => {
    if (s.status !== "processing") return;
    const t = setInterval(async () => {
      const st = await poll();
      if (st === "ready") toast.success(`"${s.name}" is ready`);
      if (st === "failed") toast.error(`"${s.name}" failed to process`);
    }, 3000);
    return () => clearInterval(t);
  }, [s.status, poll, s.name]);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateStorySection(s.id, {
        name: s.name,
        internal_description: s.internal_description,
        headline: s.headline,
        body: s.body,
        headline_hi: s.headline_hi,
        body_hi: s.body_hi,
        overlay_position: s.overlay_position,
        animation_preset: s.animation_preset,
      });
      setS((p) => ({ ...p, ...updated }));
      toast.success("Section saved");
    } catch {
      toast.error("Could not save section");
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please choose a video file");
      return;
    }
    setUploadPct(0);
    setS((p) => ({ ...p, status: "uploading" }));
    try {
      await uploadSectionVideo(s.id, file, setUploadPct);
      setUploadPct(null);
      setS((p) => ({ ...p, status: "processing" }));
      toast.success("Upload complete — generating scroll animation…");
    } catch {
      setUploadPct(null);
      setS((p) => ({ ...p, status: "failed", error: "Upload failed" }));
      toast.error("Upload failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete section "${s.name}"?`)) return;
    try {
      await deleteStorySection(s.id);
      onDeleted(s.id);
      toast.success("Section deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const st = STATUS[s.status] || STATUS.empty;
  const StIcon = st.Icon;
  const spinning = s.status === "processing" || s.status === "uploading";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={`story-section-card-${index}`}
      className="rounded-2xl border border-amber-300/12 bg-[#0d1226]/70 p-5"
    >
      <div className="mb-4 flex items-center gap-3">
        <span
          {...dragHandleProps}
          data-testid={`story-drag-${index}`}
          className="cursor-grab text-amber-100/30 transition-colors hover:text-amber-200 active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </span>
        <span className="grid h-7 w-7 place-items-center rounded-full border border-amber-300/30 text-xs font-semibold text-amber-200">
          {index + 1}
        </span>
        <span
          data-testid={`story-status-${index}`}
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wider ${st.cls}`}
        >
          <StIcon className={`h-3 w-3 ${spinning ? "animate-spin" : ""}`} />
          {st.label}
          {s.status === "ready" && ` · ${s.frame_count} frames`}
          {uploadPct !== null && ` ${uploadPct}%`}
        </span>
        <button
          data-testid={`story-delete-${index}`}
          onClick={remove}
          className="ml-auto rounded-full border border-red-400/20 p-1.5 text-red-300/80 transition-all hover:border-red-400/50 hover:bg-red-500/10"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-[160px_1fr]">
        {/* layout skeleton preview + upload */}
        <div>
          <SceneSkeleton s={s} index={index} />
          <input
            ref={fileRef}
            data-testid={`story-video-input-${index}`}
            type="file"
            accept="video/*"
            onChange={onFile}
            className="hidden"
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={spinning}
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-300/25 px-3 py-2 text-xs font-medium text-amber-200 transition-all hover:border-amber-300/60 hover:bg-amber-400/10 disabled:opacity-50"
          >
            <UploadCloud className="h-3.5 w-3.5" />
            {s.video ? "Replace video" : "Upload MP4 (≤30s)"}
          </button>
          {s.status === "failed" && s.error && (
            <p className="mt-2 text-[11px] text-red-300/80">{s.error}</p>
          )}
        </div>

        {/* fields */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field
              label="Section name"
              value={s.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <Field
              label="Internal description (admin only)"
              value={s.internal_description}
              onChange={(e) => set("internal_description", e.target.value)}
            />
          </div>
          <Field
            label="Headline (English)"
            value={s.headline}
            onChange={(e) => set("headline", e.target.value)}
          />
          <Field
            label="Headline (Hindi)"
            value={s.headline_hi}
            onChange={(e) => set("headline_hi", e.target.value)}
          />
          <Field
            label="Body (English)"
            textarea
            value={s.body}
            onChange={(e) => set("body", e.target.value)}
          />
          <Field
            label="Body (Hindi)"
            textarea
            value={s.body_hi}
            onChange={(e) => set("body_hi", e.target.value)}
          />
          <Select
            label="Overlay position"
            testId={`story-overlay-${index}`}
            value={s.overlay_position}
            onChange={(e) => set("overlay_position", e.target.value)}
            options={["left", "center", "bottom"]}
          />
          <Select
            label="Animation preset"
            testId={`story-preset-${index}`}
            value={s.animation_preset}
            onChange={(e) => set("animation_preset", e.target.value)}
            options={["story", "cover"]}
          />
          <div className="sm:col-span-2">
            <button
              data-testid={`story-save-${index}`}
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-4 py-2 text-xs font-semibold text-[#0a0f24] transition-all hover:bg-amber-300 disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save section
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const StorySectionsPanel = ({ campaignId }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const dragIndex = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setSections(await listStorySections(campaignId));
    } catch {
      toast.error("Could not load story sections");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const add = async () => {
    setAdding(true);
    try {
      const created = await createStorySection(campaignId, {
        name: `Scene ${sections.length + 1}`,
      });
      setSections((p) => [...p, created]);
    } catch {
      toast.error("Could not add section");
    } finally {
      setAdding(false);
    }
  };

  const onDrop = (to) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from == null || from === to) return;
    setSections((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      reorderStorySections(
        campaignId,
        next.map((s) => s.id)
      ).catch(() => toast.error("Could not save order"));
      return next;
    });
  };

  return (
    <div className="mt-10">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-100/80">
            Scroll Story (video → animation)
          </h2>
          <p className="mt-1 text-xs text-amber-100/40">
            Upload a cinematic clip (≤30s) per scene — it auto-converts into a
            frame-by-frame scroll animation. Microsites with ready scenes use the
            new Story engine.
          </p>
        </div>
        <button
          data-testid="story-add-section-btn"
          onClick={add}
          disabled={adding}
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/25 px-3.5 py-1.5 text-xs font-medium text-amber-200 transition-all hover:border-amber-300/60 hover:bg-amber-400/10 disabled:opacity-50"
        >
          {adding ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          Add scene
        </button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-10 text-amber-100/40">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : sections.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-amber-300/15 py-10 text-center text-sm text-amber-100/40">
          No scenes yet. Add a scene and upload a clip to build a cinematic scroll story.
        </p>
      ) : (
        <div className="space-y-4" data-testid="story-sections-list">
          {sections.map((sec, i) => (
            <div
              key={sec.id}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(i)}
            >
              <StorySectionCard
                section={sec}
                index={i}
                dragHandleProps={{
                  draggable: true,
                  onDragStart: () => {
                    dragIndex.current = i;
                  },
                  onDragEnd: () => {
                    dragIndex.current = null;
                  },
                }}
                onDeleted={(id) =>
                  setSections((p) => p.filter((x) => x.id !== id))
                }
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
